import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import {
  APP_ZONES,
  AllowedCountryType,
  BaseCssProperties,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  UiZoneWidgetConfig,
  baseCssConfigStyle,
  buttonNestedWidgetConfigStyles,
} from '@finxone-platform/shared/sys-config-types';

import { Country } from '@app/finxone-web-frontend/app/lib/dtos/countries.dto';
import { Currency } from '@app/finxone-web-frontend/app/lib/dtos/currencies.dto';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { AccountState } from '@app/finxone-web-frontend/app/lib/state/account.state';
import {
  countryCodeToCircleFlag,
  currencyToCircleFlag,
} from '@app/finxone-web-frontend/app/lib/utils/template/handlebar-helpers/currency-to-flag.utils';
import {
  removeDuplicateCurrencies,
  removeInvalidCurrencies,
} from '@app/finxone-web-frontend/app/lib/utils/utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { Store } from '@ngxs/store';
import { Subject, map, take, takeUntil } from 'rxjs';
import { BaseComponent } from '../base/base.component';
@Component({
  standalone: false,
  selector: 'finxone-web-frontend-international-currency-country-widget',
  templateUrl: './international-currency-country-widget.component.html',
})
export class InternationalCurrencyCountryWidgetComponent extends BaseComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  countryList: AllowedCountryType[];
  public currencies: Currency[];
  public currenciesCodes: {
    title: string;
    currencyFlag?: string;
    value: string;
  }[] = [];
  public widgetConfig: UiZoneWidgetConfig;
  public inputFieldBaseStyle: baseCssConfigStyle;
  public inputFieldInactiveStyle: baseCssConfigStyle;
  public currentStyle: baseCssConfigStyle;
  public inputFieldStyle: baseCssConfigStyle;
  public countries: Country[];
  public currentActiveStyle: baseCssConfigStyle;
  public continueButtonStyle: baseCssConfigStyle = {};
  public continueButtonContent = 'Continue';
  public supportedCurrencies: string[];
  public showCurrencyFlags = true;
  public globalStylingClass = '';
  public isAllowedGlobalStyling = false;
  public isNewAddBeneficiaryIntlExist = false;

  constructor(
    public dataIn: ProviderClass<UiZoneWidgetAttributeConfig>,
    private configService: ConfigService,
    private store: Store,
    private cdr: ChangeDetectorRef,
  ) {
    super();
    this.store
      .selectOnce(AccountState.getPaymentGatewayConfig)
      .pipe(
        map((paymentGatewayConfig) => {
          this.supportedCurrencies = paymentGatewayConfig.supportedCurrency ?? ['GBP', 'USD', 'EUR'];
        }),
      )
      .subscribe();

    this.configService
      .getCurrencies()
      .pipe(
        takeUntil(this.destroy$),
        map((response) => {
          const validCurrencies = removeInvalidCurrencies(response);
          this.currencies = removeDuplicateCurrencies(validCurrencies, 'currency_code');
          this.currencies = this.currencies.filter((currency) =>
            this.supportedCurrencies.includes(currency.currency_code),
          );
          this.currenciesCodes = this.currencies.map(({ currency_code }) => ({
            title: currency_code,
            currencyFlag: currencyToCircleFlag(currency_code, '24px'),
            value: currency_code,
          }));
        }),
      )
      .subscribe();
  }
  ngOnInit() {
    if (this.dataIn != undefined && this.dataIn != null) {
      this.widgetConfig = this.dataIn.widgetConfig;
      this.attri = this.dataIn.attri;
    }

    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.PADDING,
      BaseCssProperties.BACKGROUND_COLOR,
      BaseCssProperties.MARGIN,
      BaseCssProperties.BORDER,
      BaseCssProperties.BOX_SHADOW,
      BaseCssProperties.COLOR,
    ]);

    this.continueButtonContent =
      this.dataIn.widgetConfig.nestedWidgets?.['continueButton'].widgetProperties.textContent ?? 'Continue';
    this.continueButtonStyle = this.configService.getSpecificBaseCssStyle(
      this.getNestedWidgetConfig(this.dataIn, 'continueButton'),
      buttonNestedWidgetConfigStyles,
    );

    this.getAllCountriesForList();

    this.showCurrencyFlags = this.attri?.widgetProperties?.showFlag ?? true;

    const { baseStyle, activeStyle, inactiveStyle } = this.configService.getInputFieldCssStyles(
      this.attri.widgetProperties,
    );
    this.inputFieldStyle = baseStyle;
    this.inputFieldBaseStyle = activeStyle;
    this.inputFieldInactiveStyle = inactiveStyle;
    this.currentStyle = {
      ...this.inputFieldInactiveStyle,
      ...this.inputFieldBaseStyle,
    };
    this.currentActiveStyle = {
      ...this.inputFieldStyle,
      ...this.inputFieldBaseStyle,
    };
    this.getGlobalStyling();
    this.validateNewAddBeneficiaryIntlPageExist();
  }

  private getGlobalStyling() {
    if (
      this.dataIn.widgetConfig.nestedWidgets?.['continueButton'].widgetProperties?.['globalStylingClass'] &&
      this.dataIn.widgetConfig.nestedWidgets?.['continueButton'].widgetProperties?.['globalStyling']
    ) {
      this.globalStylingClass =
        this.dataIn.widgetConfig.nestedWidgets?.['continueButton'].widgetProperties?.['globalStylingClass'] ??
        '';
      this.isAllowedGlobalStyling =
        this.dataIn.widgetConfig.nestedWidgets?.['continueButton'].widgetProperties?.['globalStyling'] ??
        false;
    }
  }

  private getAllCountriesForList() {
    this.configService
      .getCountries()
      .pipe(
        takeUntil(this.destroy$),
        map((response) => {
          this.countries = response;
          this.countryList = this.countries.map(({ name, code }) => ({
            title: name,
            currencyFlag: countryCodeToCircleFlag(code, '24px'),
            key: code,
          }));
        }),
      )
      .subscribe();
  }

  private validateNewAddBeneficiaryIntlPageExist() {
    this.configService
      .getPageConfigByUrlAndZone(APP_ZONES.PAYMENT, 'add-beneficiary-intl')
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.isNewAddBeneficiaryIntlExist = true;
        }
      });
  }
}
